import * as types from './mutation-types'
import axios from 'axios'
import {
  getAccountsAPIData,
  batchCreateAccounts,
  getCreateAssessmentAPIData,
  getUpdateAssessmentAPIData
} from '@/services/Accounts'

/**
 * Create new user accounts
 *
 * @param commit
 * @param rootState
 * @param data
 * @returns {Promise<any>}
 */
export const createAccounts = ({ commit, getters }, data) => {
  return batchCreateAccounts(data)
    .then((result) => {
      commit(types.CREATE_ACCOUNTS)
      return result
    })
}

/**
 * Update user accounts
 *
 * @param commit
 * @param rootState
 * @param data
 */
export const updateAccounts = ({ commit, rootState }, updateData) => {
  return Promise.resolve()
    .then(() => {
      // If we override the current assessment, we will send a PUT request to /api/v1/assessments
      // Otherwise we will create a new assessment with POST to '/api/v1/assessments'

      if (updateData.updateType === 'override') {
        const userIds = updateData.users.map(user => user.id)
        const userData = getAccountsAPIData(updateData).users

        if (updateData.customConfig !== null && (updateData.customConfig.tasks.names.length === 0 && Object.keys(updateData.customConfig.tasks.parameters).length === 0)) {
          updateData.customConfig = null
          delete userData[0].assessment_config.tasks
        }

        return axios.put('/api/v1/assessments', getUpdateAssessmentAPIData(userIds, userData[0], updateData.updateAssessmentsStarted))
      } else {
        // If we have an assessmentConfig id, the existing tasks need to be deleted from the config if they exist, so that the
        // normal config can re-apply as expected.
        if (updateData.assessmentConfigId && updateData.user.assessment_config && 'tasks' in updateData.user.assessment_config) {
          delete updateData.user.assessment_config.tasks
        }
        const atsProfileIds = updateData.users.map(user => user.assessments[0].ats_profile_id)
        const userData = getAccountsAPIData(updateData).users
        return axios.post('/api/v1/assessments', getCreateAssessmentAPIData(atsProfileIds, userData))
      }
    })
    .then((resp) => {
      commit(types.UPDATE_ACCOUNTS)
      return resp.data.users.length
    })
}

/**
 * Change the recent accounts threshold
 *
 * @param commit
 * @param state
 * @param days
 * @returns {Promise<any>}
 */
export const changeRecentThreshold = ({ commit, state }, days) => {
  return new Promise((resolve) => {
    commit(types.CHANGE_THRESHOLD, { days })
    resolve()
  })
}

/**
 * Reset stored data
 *
 * @param commit
 */
export const clear = ({ commit }) => {
  commit(types.CLEAR)
}

/**
 * Creates a retest assessment.
 *
 * @param {Object} context - The Vuex context object.
 * @param {Object} data - The data for creating the retest assessment.
 * @param {Array} data.retestTasks - The list of retest tasks.
 * @param {number} data.retestReason - The ID of the retest reason.
 * @param {Object} data.user - The user object.
 * @param {Array} data.user.assessments - The list of user assessments.
 * @param {number} data.user.assessments[0].id - The ID of the assessment.
 * @param {string} [data.end_date] - The optional end date.
 * @returns {Promise<number>} A promise that resolves to the number of users in the response data.
 */
export const createRetestAssessment = ({ commit }, data) => {
  const taskList = data.retestTasks
  const reasonId = data.retestReason
  const assessment = data.user.assessments[0]
  const requestBody = {
    task_list: taskList,
    reason_id: reasonId,
    assessment_id: assessment.id
  }
  const updateAssessmentEndDate = data.user.assessment_end_date
  if (updateAssessmentEndDate && updateAssessmentEndDate !== assessment.end_date) {
    requestBody.end_date = updateAssessmentEndDate
  }

  // do an axios.post to /api/v1/session/retest with request data json using the above variables
  // return the response
  return axios.post('/api/v1/session/retest', requestBody)
    .then((resp) => {
      commit(types.UPDATE_ACCOUNTS)
      // return the number of updates in the response data
      return 1
    }).catch((error) => {
      console.error(error)
      throw error
    })
}

/**
 * get the retests for this assessment if exists
 * @param assessmentId
 * @returns {Promise<any>}
 */
export async function getRetests ({ commit }, assessmentId) {
  // do an axios get call to /api/v1/session/retests?assessment_id=assessmentId
  // response { retests: [{ retest_id: number, retest_tasks: string[], reason: string, created_at: date, created_by: string }]}
  if (!assessmentId) {
    return Promise.resolve([])
  }
  return axios.get(`/api/v1/session/retest?assessment_id=${assessmentId}`)
    .then((response) => {
      commit(types.GET_RETESTS, { assessmentId: assessmentId, retests: response.data.retests })
      return response.data.retests
    })
}

/**
 * get the retests reasons
 * @returns {Promise<any>}
 */
export async function getRetestReasons ({ commit }) {
  // do an axios get call to /api/v1/session/retests/reasons to receive the list of options for a retest
  // response { items: [{ reason_id: number, reason: string }]}
  return axios.get('/api/v1/retests/reasons')
    .then((response) => {
      commit(types.GET_RETEST_REASONS, response.data.items)
      return response.data.items
    })
}

export default {
  createAccounts,
  updateAccounts,
  changeRecentThreshold,
  clear,
  createRetestAssessment,
  getRetests,
  getRetestReasons
}
